import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { useParams } from "react-router-dom";
import {
  PenLine,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  FileSignature,
  Calendar,
  DollarSign,
  Clock,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

const LeaseAgreement = ({ studentId: propStudentId, showFinalizeButton = false }) => {
  const { studentId: paramStudentId } = useParams();
  const studentId = propStudentId || paramStudentId;

  const [leaseUrl, setLeaseUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [showSignaturePopup, setShowSignaturePopup] = useState(false);
  const [showFinalizePopup, setShowFinalizePopup] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [isLeaseSigned, setIsLeaseSigned] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const [finalizeData, setFinalizeData] = useState({
    startDate: "",
    endDate: "",
    leaseAmount: "",
    depositAmount: "",
  });

  const sigCanvas = useRef(null);

  const fetchLeasePDF = async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/LeaseGenerator/view/${studentId}`,
        { responseType: "blob" }
      );
      const file = new Blob([response.data], { type: "application/pdf" });
      setLeaseUrl(URL.createObjectURL(file));
    } catch (err) {
      console.error(err);
      setLeaseUrl("");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaseInfo = async () => {
    if (!studentId) return;
    try {
      const { data } = await axios.get(
        `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/LeaseGenerator/info/${studentId}`
      );
      setIsLeaseSigned(data.isSigned || false);
      setIsFinalized(data.isFinalized || false);
      if (data.isSigned) setSignaturePreview("already-signed");
    } catch (err) {
      console.error(err);
      setIsLeaseSigned(false);
      setIsFinalized(false);
    }
  };

  useEffect(() => {
    fetchLeasePDF();
    fetchLeaseInfo();
    return () => leaseUrl && URL.revokeObjectURL(leaseUrl);
  }, [studentId]);

  const clearSignature = () => {
    sigCanvas.current?.clear();
    setSignaturePreview(null);
  };

  const handleSaveSignature = async () => {
    if (!studentId) return toast.error("Student ID missing");
    if (!sigCanvas.current || sigCanvas.current.isEmpty())
      return toast.error("Please draw your signature first.");

    const signatureImage = sigCanvas.current.getCanvas().toDataURL("image/png");

    try {
      await axios.post(
        `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/LeaseGenerator/sign/${studentId}`,
        { signature: signatureImage }
      );

      setSignaturePreview(signatureImage);
      setIsLeaseSigned(true);
      setShowSignaturePopup(false);

      await fetchLeasePDF();
      await fetchLeaseInfo();

      toast.success("Lease signed successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Your Lease isn't Yet Finalized.");
    }
  };

  const handleFinalizeSubmit = async () => {
    const { startDate, endDate, leaseAmount, depositAmount } = finalizeData;
    if (!startDate || !endDate || !leaseAmount || !depositAmount) {
      toast.error("Please fill in all fields.");
      return;
    }

    setLoading(true);
    try {
      const formattedStartDate = new Date(startDate).toISOString().split("T")[0];
      const formattedEndDate = new Date(endDate).toISOString().split("T")[0];

      await axios.post(
        `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/LeaseGenerator/finalize/${studentId}`,
        {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          leaseAmount: parseFloat(leaseAmount),
          depositAmount: parseFloat(depositAmount),
        }
      );

      toast.success("Lease finalized successfully!");

      // Generate invoice
      try {
        const invoiceResponse = await axios.post(
          `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/Invoice/generate/${studentId}`
        );
        toast.success(`Invoice generated: ${invoiceResponse.data.invoiceNumber}`);
      } catch (invoiceErr) {
        console.error(invoiceErr);
        toast.error("Failed to generate invoice.");
      }

      setShowFinalizePopup(false);
      setFinalizeData({ startDate: "", endDate: "", leaseAmount: "", depositAmount: "" });
      setIsFinalized(true);
      await fetchLeasePDF();
      await fetchLeaseInfo();
    } catch (err) {
      console.error(err);
      toast.error("Failed to finalize lease.");
    } finally {
      setLoading(false);
    }
  };

  // Shared input style — explicit text color so typed values are visible
  const inputClass =
    "w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-900 placeholder-gray-400 bg-white focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all duration-200";

  // Determine overall lease stage for the status card
  const stage = isFinalized ? "finalized" : isLeaseSigned ? "signed" : "pending";
  const stageInfo = {
    pending: {
      label: "Awaiting signature",
      desc: "Review the agreement below and sign to continue",
      color: "amber",
    },
    signed: {
      label: "Signed, awaiting finalization",
      desc: "Your signature has been recorded. Administration will finalize the lease.",
      color: "blue",
    },
    finalized: {
      label: "Fully finalized",
      desc: "The lease has been signed and finalized. An invoice has been generated.",
      color: "emerald",
    },
  };
  const accent = stageInfo[stage];
  const accentClasses = {
    amber: { bg: "from-amber-50 to-amber-100", border: "border-amber-100", icon: "text-amber-600", iconBg: "bg-white" },
    blue: { bg: "from-blue-50 to-blue-100", border: "border-blue-100", icon: "text-blue-600", iconBg: "bg-white" },
    emerald: { bg: "from-emerald-50 to-emerald-100", border: "border-emerald-100", icon: "text-emerald-600", iconBg: "bg-white" },
  }[accent.color];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <Toaster
        position="top-right"
        reverseOrder={false}
        toastOptions={{
          style: {
            borderRadius: '12px',
            background: '#fff',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />

      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-700 p-6 md:p-8 text-white shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="p-3 bg-white/15 backdrop-blur rounded-2xl shadow-inner">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Lease agreement</h1>
            <p className="text-blue-100 text-sm mt-1">
              Review your lease, sign it electronically, and track its finalization status
            </p>
          </div>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className={`bg-gradient-to-br ${accentClasses.bg} rounded-xl p-5 shadow-sm border ${accentClasses.border} sm:col-span-2`}>
          <div className="flex items-start gap-4">
            <div className={`p-2.5 ${accentClasses.iconBg} rounded-xl shadow-sm flex-shrink-0`}>
              {stage === "finalized" ? (
                <CheckCircle2 className={`w-5 h-5 ${accentClasses.icon}`} />
              ) : stage === "signed" ? (
                <Clock className={`w-5 h-5 ${accentClasses.icon}`} />
              ) : (
                <PenLine className={`w-5 h-5 ${accentClasses.icon}`} />
              )}
            </div>
            <div>
              <p className={`text-sm font-semibold ${accentClasses.icon}`}>{accent.label}</p>
              <p className="text-sm text-gray-600 mt-1">{accent.desc}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                  isLeaseSigned ? "bg-white text-green-700 border-green-200" : "bg-white/70 text-gray-400 border-gray-200"
                }`}>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Signed
                </span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                  isFinalized ? "bg-white text-indigo-700 border-indigo-200" : "bg-white/70 text-gray-400 border-gray-200"
                }`}>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Finalized
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-violet-50 to-violet-100 rounded-xl p-5 shadow-sm border border-violet-100">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2.5 bg-white rounded-xl shadow-sm">
              <FileSignature className="w-5 h-5 text-violet-600" />
            </div>
          </div>
          <p className="text-sm text-violet-700 font-medium">Document</p>
          <p className="text-lg font-bold text-violet-900 mt-1">{leaseUrl ? "Available" : "Not generated"}</p>
          <p className="text-xs text-violet-500 mt-2">Your lease PDF, viewable below</p>
        </div>
      </div>

      {/* Lease Viewer Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg">
              <FileSignature className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Lease document</h2>
              <p className="text-sm text-gray-500">Review your lease agreement below before signing</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-80">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin mb-4" />
              <p className="text-sm text-gray-500 font-medium">Loading lease document...</p>
              <p className="text-xs text-gray-400 mt-1">Please wait while we prepare your agreement</p>
            </div>
          ) : leaseUrl ? (
            <iframe
              src={leaseUrl}
              title="Lease Agreement"
              className="w-full h-[650px] bg-white"
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-80">
              <div className="p-4 bg-indigo-50 rounded-full mb-4">
                <FileSignature className="w-12 h-12 text-indigo-300" />
              </div>
              <p className="text-gray-500 font-medium">No lease agreement available</p>
              <p className="text-sm text-gray-400 mt-1">Please contact administration for assistance</p>
            </div>
          )}
        </div>
      </div>

      {/* Actions Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="mb-4">
          <h3 className="text-base font-semibold text-gray-900">Actions</h3>
          <p className="text-sm text-gray-500 mt-0.5">Sign your lease electronically or finalize the agreement</p>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowSignaturePopup(true)}
              disabled={isLeaseSigned}
              className={`
                flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold 
                transition-all duration-200 shadow-sm
                ${isLeaseSigned
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:shadow-md"
                }
              `}
            >
              <PenLine className="w-4 h-4" />
              Sign lease
            </button>

            {showFinalizeButton && (
              <button
                onClick={() => setShowFinalizePopup(true)}
                disabled={isFinalized}
                className={`
                  flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold 
                  transition-all duration-200 shadow-sm
                  ${isFinalized
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-emerald-600 to-green-600 text-white hover:from-emerald-700 hover:to-green-700 hover:shadow-md"
                  }
                `}
              >
                <CheckCircle2 className="w-4 h-4" />
                Finalize lease
              </button>
            )}
          </div>

          {/* Status Badges */}
          <div className="flex gap-2">
            {isLeaseSigned && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Lease signed
              </span>
            )}

            {isFinalized && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Lease finalized
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Signature Preview Card */}
      {signaturePreview && signaturePreview !== "already-signed" && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 max-w-md">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <PenLine className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm">Signature recorded</h4>
              <p className="text-xs text-gray-500">Your electronic signature has been captured</p>
            </div>
          </div>
          <img
            src={signaturePreview}
            alt="Signature Preview"
            className="border border-gray-200 rounded-xl p-4 bg-gray-50"
          />
        </div>
      )}

      {/* Signature Modal */}
      {showSignaturePopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
              <div className="flex items-center gap-2">
                <PenLine className="w-5 h-5 text-white" />
                <h3 className="text-lg font-semibold text-white">Sign lease agreement</h3>
              </div>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Please draw your signature in the box below to electronically sign the lease agreement.
              </p>

              <SignatureCanvas
                ref={sigCanvas}
                penColor="#1e293b"
                canvasProps={{
                  className: "border-2 border-gray-300 rounded-xl w-full h-48 mb-4 bg-white cursor-crosshair",
                }}
              />

              <div className="flex justify-end gap-3">
                <button
                  onClick={clearSignature}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all duration-200"
                >
                  Clear
                </button>
                <button
                  onClick={handleSaveSignature}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-sm"
                >
                  Submit signature
                </button>
                <button
                  onClick={() => setShowSignaturePopup(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-red-500 text-white hover:bg-red-600 transition-all duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Finalize Modal */}
      {showFinalizePopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-white" />
                <h3 className="text-lg font-semibold text-white">Finalize lease agreement</h3>
              </div>
              <button
                onClick={() => setShowFinalizePopup(false)}
                className="text-white/80 hover:text-white transition"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-6">
                Please provide the following information to finalize the lease agreement.
              </p>

              <div className="space-y-4">
                {/* Start Date */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Start date
                  </label>
                  <input
                    type="date"
                    value={finalizeData.startDate}
                    onChange={(e) =>
                      setFinalizeData({
                        ...finalizeData,
                        startDate: e.target.value,
                      })
                    }
                    className={inputClass}
                  />
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    End date
                  </label>
                  <input
                    type="date"
                    value={finalizeData.endDate}
                    onChange={(e) =>
                      setFinalizeData({
                        ...finalizeData,
                        endDate: e.target.value,
                      })
                    }
                    className={inputClass}
                  />
                </div>

                {/* Lease Amount */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <DollarSign className="w-4 h-4 inline mr-2" />
                    Lease amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={finalizeData.leaseAmount}
                    onChange={(e) =>
                      setFinalizeData({
                        ...finalizeData,
                        leaseAmount: e.target.value,
                      })
                    }
                    className={inputClass}
                  />
                </div>

                {/* Deposit Amount */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <DollarSign className="w-4 h-4 inline mr-2" />
                    Deposit amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={finalizeData.depositAmount}
                    onChange={(e) =>
                      setFinalizeData({
                        ...finalizeData,
                        depositAmount: e.target.value,
                      })
                    }
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8">
                <button
                  onClick={() => setShowFinalizePopup(false)}
                  className="px-5 py-2.5 text-sm font-medium rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFinalizeSubmit}
                  disabled={loading}
                  className={`
                    flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl 
                    transition-all duration-200 shadow-sm
                    ${loading
                      ? "bg-emerald-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-emerald-600 to-green-600 text-white hover:from-emerald-700 hover:to-green-700 hover:shadow-md"
                    }
                  `}
                >
                  {loading && (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {loading ? "Finalizing..." : "Submit & generate invoice"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaseAgreement;